import { useState, type ElementType, type ReactNode } from "react";
import { Link } from "react-router-dom";
import moment from "moment/moment";
import {
  IconArrowLeft,
  IconCheck,
  IconCircleCheck,
  IconClipboardCheck,
  IconFlag,
  IconMail,
  IconRotateClockwise,
  IconSearch,
  IconShieldCheck,
  IconTrash,
} from "@tabler/icons-react";
import Button from "../../UI/Button";
import Callout from "../../UI/Callout";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../../UI/ModalDialog";
import StepBadge from "../../UI/StepBadge";
import Status from "../../UI/Status";
import Text from "../../UI/Text";
// @ts-expect-error pure js
import TextInputStandalone from "../../UI/TextInputStandalone";
import { useToast } from "../../UI/Toast/useToast";
// @ts-expect-error pure js
import useLoader from "../../../hooks/use-loader";
// @ts-expect-error pure js
import useProcesses from "../../../hooks/use-processes";
// @ts-expect-error pure js
import useTenants from "../../../hooks/use-tenants";
// @ts-expect-error pure js
import { TenantStatus } from "../../../models/tenant_old.js";
// @ts-expect-error pure js
import { ProcessType } from "../../../utils/constants/processes";
// @ts-expect-error pure js
import { changeTenantProrata } from "../../../utils/tenants";
// @ts-expect-error pure js
import { classnames, handleAPIError } from "../../../utils";
import type { Process } from "../../../models/process";
import type { Tenant } from "../../../models/tenant";
import styles from "./ProcessDepart.module.css";

interface StepDef {
  key: string;
  title: string;
  description: ReactNode;
  done: boolean;
  /** Action métier de l'étape (courrier à générer, état des lieux…), à gauche. */
  action?: ReactNode;
  onValidate: () => Promise<void>;
  onCancel: () => Promise<void>;
  validateDisabled?: boolean;
}

interface ProcessDepartProps {
  process: Process;
  tenant: Tenant;
  profileId?: number;
  onFinish: (processId: Process["id"]) => Promise<void>;
  onDelete: (processId: Process["id"]) => Promise<void>;
  onBack: () => void;
  /** Ferme la modale (avant de naviguer vers une action du locataire). */
  onClose: () => void;
}

/** Détail d'un processus de fin de bail : frise des étapes et actions de clôture. */
export default function ProcessDepart({
  process,
  tenant,
  profileId,
  onFinish,
  onDelete,
  onBack,
  onClose,
}: ProcessDepartProps) {
  const { updateProcessEntry } = useProcesses() as {
    updateProcessEntry: (
      id: Process["id"],
      entry: Record<string, boolean>,
    ) => Promise<void>;
  };
  const { updateTenant, updateProrataTenant } = useTenants() as {
    updateTenant: (id: number, values: Partial<Tenant>) => Promise<void>;
    updateProrataTenant: (id: number) => Promise<void>;
  };
  const toast = useToast();
  const [prorataConfirmOpen, setProrataConfirmOpen] = useState(false);
  const [finDraft, setFinDraft] = useState(tenant.fin ?? "");

  const [isFinishing, finishHandler] = useLoader(async () => {
    try {
      await updateTenant(tenant.id, { status: TenantStatus.Archive.status });
      await onFinish(process.id);
    } catch (error) {
      toast.error({
        title: "Impossible de terminer le processus",
        description: handleAPIError(error),
      });
    }
  });

  const [isDeleting, deleteHandler] = useLoader(async () => {
    try {
      await onDelete(process.id);
    } catch (error) {
      toast.error({
        title: "Impossible de supprimer le processus",
        description: handleAPIError(error),
      });
    }
  });

  async function toggleEntry(key: string, value: boolean) {
    try {
      await updateProcessEntry(process.id, { [key]: value });
    } catch (error) {
      toast.error({
        title: "Impossible de mettre à jour l'étape",
        description: handleAPIError(error),
      });
    }
  }

  async function saveTenant(values: Partial<Tenant>) {
    try {
      await updateTenant(tenant.id, values);
      if (changeTenantProrata(tenant, { fin: tenant.fin, ...values })) {
        setProrataConfirmOpen(true);
      }
    } catch (error) {
      toast.error({
        title: "Impossible de mettre à jour le bail",
        description: handleAPIError(error),
      });
    }
  }

  async function confirmProrata() {
    setProrataConfirmOpen(false);
    try {
      await updateProrataTenant(tenant.id);
    } catch (error) {
      toast.error({
        title: "Impossible de mettre à jour le prorata",
        description: handleAPIError(error),
      });
    }
  }

  function actionLink(label: string, icon: ElementType, to: string) {
    return (
      <Button
        variant="secondary"
        size="sm"
        icon={icon}
        render={<Link to={to} onClick={onClose} />}
      >
        {label}
      </Button>
    );
  }

  function entryStep(
    key: string,
    title: string,
    description: ReactNode,
    action?: ReactNode,
  ): StepDef {
    return {
      key,
      title,
      description,
      action,
      done: !!process.entries?.[key],
      onValidate: () => toggleEntry(key, true),
      onCancel: () => toggleEntry(key, false),
    };
  }

  const actionsUrl = `/bailleur/tenants/${tenant.id}?tab-tenant-show=actions`;
  const finLabel = tenant.fin
    ? moment(tenant.fin).format("DD/MM/YYYY")
    : undefined;

  const steps: StepDef[] = [];
  if (process.type === ProcessType.DEPART) {
    steps.push({
      key: "preavis",
      title: "Réception du préavis",
      description: tenant.preavis
        ? `Votre locataire a donné son préavis de départ pour le ${finLabel}. Vous pouvez ajuster la date de fin de bail si besoin.`
        : "Votre locataire n'a pas encore déposé son préavis. S'il l'a déjà donné, renseignez la date de fin de bail puis validez l'étape.",
      done: !!tenant.preavis,
      action: (
        <TextInputStandalone
          type="date"
          name="fin"
          aria-label="Date de fin de bail"
          value={finDraft}
          className={styles.dateInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFinDraft(e.target.value)
          }
          onBlur={() => {
            if (tenant.preavis && finDraft && finDraft !== tenant.fin) {
              void saveTenant({ fin: finDraft });
            }
          }}
        />
      ),
      validateDisabled: !finDraft,
      onValidate: () => saveTenant({ fin: finDraft, preavis: true }),
      onCancel: () => saveTenant({ preavis: false }),
    });
  }
  if (process.type === ProcessType.DEPART_BAILLEUR) {
    steps.push(
      entryStep(
        "envoie_conge",
        "Envoyer le congé",
        "Complétez et envoyez le congé pour mettre fin au bail de votre locataire. Attention à bien respecter le délai du préavis.",
        actionLink(
          "Générer le congé",
          IconMail,
          `${actionsUrl}&action=avenant&avenant-type=rappel_fin_bail`,
        ),
      ),
      entryStep(
        "ar_conge",
        "Accusé de réception du courrier",
        "Le délai de préavis ne commence à courir qu'à compter de la réception du congé par le locataire. Vérifiez qu'il a bien été reçu.",
      ),
    );
  }
  steps.push(
    entryStep(
      "rappel_fin_bail",
      "Rappel de fin de bail",
      "Envoyez un courrier à votre locataire pour lui rappeler la date de fin de bail et les consignes de sortie.",
      actionLink(
        "Générer le courrier",
        IconMail,
        `${actionsUrl}&action=courrier&avenant-type=rappel_fin_bail`,
      ),
    ),
    entryStep(
      "convocation_edl",
      "Convocation à l'état des lieux",
      "Informez le locataire de la date et de l'heure de l'état des lieux de sortie.",
      actionLink(
        "Générer le courrier",
        IconMail,
        `${actionsUrl}&action=courrier&avenant-type=convocation_edl`,
      ),
    ),
    entryStep(
      "edl",
      "Réaliser l'état des lieux",
      "C'est le jour J. Effectuez l'état des lieux de sortie. Soyez précis et pensez à prendre des photos.",
      actionLink(
        "Réaliser l'état des lieux",
        IconClipboardCheck,
        `/bailleur/tenants/${tenant.id}?action=edl`,
      ),
    ),
    entryStep(
      "dg",
      "Dépôt de garantie",
      "Si l'état des lieux est conforme, restituez le dépôt de garantie. Sinon, appliquez une retenue justifiée.",
      actionLink(
        "Gérer le dépôt de garantie",
        IconShieldCheck,
        `${actionsUrl}&action=courrier&avenant-type=restitution_dg`,
      ),
    ),
    entryStep(
      "affectation_dg",
      "Vérification du virement du dépôt",
      "Si vos comptes bancaires sont synchronisés avec Qalimo, vérifiez que le virement de remboursement est bien affecté au locataire, catégorie « remboursement dépôt de garantie ».",
      actionLink(
        "Vérifier l'opération",
        IconSearch,
        "/bailleur/finances?tab-bank=transactions",
      ),
    ),
  );

  const doneCount = steps.filter((step) => step.done).length;
  const allDone = doneCount === steps.length;
  const firstPendingIndex = steps.findIndex((step) => !step.done);

  return (
    <>
      <ModalDialogBody className={styles.body}>
        <div className={styles.topRow}>
          <Button variant="tertiary" icon={IconArrowLeft} onClick={onBack}>
            Retour à la liste des processus
          </Button>
          <Text weight="bold" color="muted" className={styles.count}>
            <IconCircleCheck size={18} />
            {doneCount} / {steps.length} étapes validées
          </Text>
        </div>
        <ol className={styles.timeline}>
          {steps.map((step, index) => (
            <li
              key={step.key}
              className={classnames(styles.step, step.done && styles.stepDone)}
            >
              <StepBadge
                number={index + 1}
                status={
                  step.done
                    ? "done"
                    : index === firstPendingIndex
                    ? "active"
                    : "pending"
                }
              />
              <div className={styles.card}>
                <div className={styles.cardHead}>
                  <div>
                    <Text variant="deci" weight="bold" className={styles.label}>
                      Étape {index + 1}
                    </Text>
                    <Text weight="bold">{step.title}</Text>
                  </div>
                  {step.done && (
                    <Status color="success" text="Validée" icon={IconCheck} />
                  )}
                </div>
                <Text>{step.description}</Text>
                <div className={styles.actions}>
                  {step.action}
                  {step.done ? (
                    <Button
                      variant="tertiary"
                      size="sm"
                      icon={IconRotateClockwise}
                      className={styles.toggle}
                      onClick={() => void step.onCancel()}
                      requiresWrite
                      profileId={profileId}
                    >
                      Annuler la validation
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={IconCheck}
                      className={styles.toggle}
                      onClick={() => void step.onValidate()}
                      disabled={step.validateDisabled}
                      requiresWrite
                      profileId={profileId}
                    >
                      Valider l'étape
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
        {allDone && (
          <Callout variant="success" title="Toutes les étapes sont validées">
            Vous pouvez terminer le processus et archiver le locataire.
          </Callout>
        )}
      </ModalDialogBody>
      <ModalDialogFooter className={styles.footer}>
        <Button
          icon={IconFlag}
          onClick={finishHandler}
          isLoading={isFinishing}
          requiresWrite
          profileId={profileId}
        >
          Terminer le processus et archiver le locataire
        </Button>
        <Button
          variant="danger"
          icon={IconTrash}
          onClick={deleteHandler}
          isLoading={isDeleting}
          requiresWrite
          profileId={profileId}
        >
          Supprimer le processus
        </Button>
      </ModalDialogFooter>
      <ModalDialog
        open={prorataConfirmOpen}
        onOpenChange={setProrataConfirmOpen}
        size="xs"
        header="Mettre à jour le prorata ?"
      >
        <ModalDialogBody>
          <Text>
            La date de fin du contrat de bail a changé. Souhaitez-vous mettre à
            jour le prorata de loyer du dernier mois dans l'historique des
            paiements ?
          </Text>
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button
            variant="secondary"
            onClick={() => setProrataConfirmOpen(false)}
          >
            Non
          </Button>
          <Button onClick={() => void confirmProrata()}>Oui</Button>
        </ModalDialogFooter>
      </ModalDialog>
    </>
  );
}
